<template>
  <div class="block">
    <SectionHeading
      :title="$t('Участники')"
      :description="
        !templateView
          ? $t(
              'Выберите допустимых участников для данного нотариального действия'
            )
          : ''
      "
    />

    <template v-if="templateView">
      <ViewGroupList :groups="viewGroups" />
    </template>

    <div v-else class="flex flex-col gap-8">
      <div class="relative w-full">
        <SearchIcon
          class="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-muted-foreground pointer-events-none"
        />
        <Input
          v-model="search"
          :placeholder="$t('Поиск участников')"
          class="pl-9"
        />
      </div>

      <!-- Groups -->
      <div class="w-full flex flex-col gap-4">
        <!-- FL GROUP -->
        <div class="flex flex-col rounded-xl">
          <div
            class="gray-block"
            @click="toggleExpand('participant_fl_type_codes')"
          >
            <Checkbox
              :model-value="isGroupChecked('participant_fl_type_codes')"
              @change="toggleGroup('participant_fl_type_codes')"
              @click.stop
              :disabled="templateView"
              :class="{ readonly: templateView }"
            >
              <span class="font-medium text-sm text-foreground !gap-3">
                {{ $t("ФЛ") }}
              </span>
            </Checkbox>

            <svg-icon
              :name="
                expandedGroups['participant_fl_type_codes']
                  ? 'huge-up'
                  : 'huge-down'
              "
              class="icon"
            />
          </div>

          <transition name="slide-fade">
            <div
              v-show="expandedGroups['participant_fl_type_codes']"
              class="flex flex-col mt-3 pl-8 gap-1"
            >
              <div class="flex flex-col gap-2.5">
                <div
                  v-for="item in participantTypes"
                  :key="item.code"
                  class="flex items-center justify-between gap-3"
                >
                  <Checkbox
                    :model-value="
                      form.participant_fl_type_codes?.includes(item.code)
                    "
                    :disabled="templateView"
                    @change="toggleItem('participant_fl_type_codes', item.code)"
                    class="flex items-center gap-3"
                  >
                    <span
                      class="flex items-center p-0 text-muted-foreground text-sm"
                    >
                      {{ item.name }}
                    </span>
                  </Checkbox>
                  <Select
                    v-if="form.participant_fl_type_codes?.includes(item.code)"
                    :model-value="getSignStatus(item.code)"
                    :disabled="templateView"
                    @update:model-value="(val) => setSignStatus(item.code, val)"
                  >
                    <SelectTrigger class="w-40 h-8 text-xs">
                      <SelectValue :placeholder="$t('Подписывает')" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="sign">
                        {{ $t("Подписывает") }}
                      </SelectItem>
                      <SelectItem value="dont_sign">
                        {{ $t("Не подписывает") }}
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>
          </transition>
        </div>

        <div class="flex flex-col rounded-xl">
          <div
            class="gray-block"
            @click="toggleExpand('participant_ul_type_codes')"
          >
            <Checkbox
              :model-value="isGroupChecked('participant_ul_type_codes')"
              @change="toggleGroup('participant_ul_type_codes')"
              @click.stop
              :disabled="templateView"
              :class="{ readonly: templateView }"
            >
              <span class="font-medium text-sm text-foreground !gap-3">
                {{ $t("ЮЛ") }}
              </span>
            </Checkbox>

            <svg-icon
              :name="
                expandedGroups['participant_ul_type_codes']
                  ? 'huge-up'
                  : 'huge-down'
              "
              class="icon"
            />
          </div>

          <transition name="slide-fade">
            <div
              v-show="expandedGroups['participant_ul_type_codes']"
              class="flex flex-col mt-3 pl-8 gap-1"
            >
              <div class="flex flex-col gap-2.5">
                <div
                  v-for="item in participantTypes"
                  :key="item.code"
                  class="flex items-center justify-between gap-3"
                >
                  <Checkbox
                    :model-value="
                      form.participant_ul_type_codes?.includes(item.code)
                    "
                    :disabled="templateView"
                    @change="toggleItem('participant_ul_type_codes', item.code)"
                    class="flex items-center gap-3"
                  >
                    <span
                      class="flex items-center p-0 text-muted-foreground text-sm"
                    >
                      {{ item.name }}
                    </span>
                  </Checkbox>
                  <Select
                    v-if="form.participant_ul_type_codes?.includes(item.code)"
                    :model-value="getSignStatus(item.code)"
                    :disabled="templateView"
                    @update:model-value="(val) => setSignStatus(item.code, val)"
                  >
                    <SelectTrigger class="w-40 h-8 text-xs">
                      <SelectValue :placeholder="$t('Подписывает')" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="sign">
                        {{ $t("Подписывает") }}
                      </SelectItem>
                      <SelectItem value="dont_sign">
                        {{ $t("Не подписывает") }}
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>
          </transition>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from "vue";
import { useRoute } from "vue-router";
import { useI18n } from "vue-i18n";
import { Search as SearchIcon } from "lucide-vue-next";
import { Input } from "@/shared/ui/input";
import ViewGroupList from "@/shared/ui/custom/ViewGroupList.vue";
import Checkbox from "@/shared/ui/checkbox/Checkbox.vue";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/shared/ui/select";
import { SectionHeading } from "@/shared/ui/section-heading";
import { useParticipantTypes } from "@/entities/directory";
import { useGroupCheckboxes } from "@/shared/lib/composables/useGroupCheckboxes";

const props = defineProps({
  form: {
    type: Object,
    default: () => ({}),
  },
});

const route = useRoute();
const { t } = useI18n();

const { participantTypes, fetchParticipantTypes } = useParticipantTypes();

const templateView = computed(
  () => route.name === "constructor_action_item_view"
);

const {
  expandedGroups,
  isGroupChecked,
  toggleGroup,
  toggleItem,
  toggleExpand,
} = useGroupCheckboxes(props.form, participantTypes, templateView);

const search = ref("");

if (!props.form.participant_dont_sign_type_codes) {
  props.form.participant_dont_sign_type_codes = [];
}

function getSignStatus(code) {
  return props.form.participant_dont_sign_type_codes?.includes(code)
    ? "dont_sign"
    : "sign";
}

function setSignStatus(code, val) {
  if (!props.form.participant_dont_sign_type_codes) {
    props.form.participant_dont_sign_type_codes = [];
  }
  const arr = props.form.participant_dont_sign_type_codes;
  const idx = arr.indexOf(code);
  if (val === "dont_sign" && idx === -1) {
    arr.push(code);
  } else if (val === "sign" && idx !== -1) {
    arr.splice(idx, 1);
  }
}

const viewGroups = computed(() => {
  const flCodes = props.form.participant_fl_type_codes || [];
  const ulCodes = props.form.participant_ul_type_codes || [];
  return [
    {
      title: t("Физическое лицо"),
      items: participantTypes.value.filter((i) => flCodes.includes(i.code)),
    },
    {
      title: t("Юридическое лицо"),
      items: participantTypes.value.filter((i) => ulCodes.includes(i.code)),
    },
  ];
});

function validate() {
  return Promise.resolve(true);
}

onMounted(async () => {
  try {
    await fetchParticipantTypes();

    if (isGroupChecked("participant_fl_type_codes")) {
      expandedGroups["participant_fl_type_codes"] = true;
    }

    if (isGroupChecked("participant_ul_type_codes")) {
      expandedGroups["participant_ul_type_codes"] = true;
    }
  } catch (error) {
    console.error("Ошибка загрузки участников:", error);
  }
});

defineExpose({ validate });
</script>

<style scoped lang="scss">
.gray-block {
  @apply w-full flex items-center px-4 py-2 gap-3 self-stretch rounded-xl;
  @apply cursor-pointer transition-colors overflow-hidden;

  background-color: hsl(var(--muted));

  &:hover {
    background-color: hsl(var(--muted));
  }

  .icon {
    @apply w-5 h-5 ml-auto text-muted-foreground transition-transform duration-200 flex-shrink-0;
  }
}

.block {
  @apply w-1/2 h-full flex flex-col p-8 gap-6 overflow-auto rounded-2xl;

  background-color: hsl(var(--background));
  border: 1px solid hsl(var(--border));
}

@media (max-width: 1500px) {
  .block {
    @apply w-full;
  }
}
</style>
